/**
 * Dynamic Sandbox config form — builds controls from schema field defs.
 */
import {
    commonFieldDefsForMode,
    defaultSandboxConfig,
    getDefaultFieldValue,
    getPath,
    modeFieldDefs,
    normalizeSandboxConfig,
    SANDBOX_BASE_MODE_TITLES,
    setPath,
    type SandboxConfig,
    type SandboxFieldDef,
} from "./sandbox-schema";
import { syncToggleSwitch } from "./toggle-switch";

let builtForMode: string | null = null;
let onChange: (() => void) | null = null;
let sandboxHelpOutsideBound = false;

function container(): HTMLElement | null {
    return document.getElementById("sandboxContainer");
}

function isCoarsePointer(): boolean {
    return typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
}

function closeAllSandboxFieldHelp(except?: HTMLElement | null): void {
    document.querySelectorAll<HTMLElement>(".sandboxFieldHelp--open").forEach((el) => {
        if (except && el === except) return;
        el.classList.remove("sandboxFieldHelp--open");
        const tip = el.querySelector(".sandboxFieldHelpTip");
        if (tip) tip.setAttribute("aria-hidden", "true");
    });
}

function ensureSandboxHelpOutsideClose(): void {
    if (sandboxHelpOutsideBound) return;
    sandboxHelpOutsideBound = true;
    const closeIfOutside = (e: Event) => {
        const t = e.target;
        if (!(t instanceof Element)) {
            closeAllSandboxFieldHelp();
            return;
        }
        if (t.closest(".sandboxFieldHelp")) return;
        closeAllSandboxFieldHelp();
    };
    document.addEventListener("pointerdown", closeIfOutside, true);
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeAllSandboxFieldHelp();
    });
}

/** In-game ? tip: hover on desktop, tap toggle on touch. Not a focusable control for the slider. */
function makeFieldHelpMark(text: string): HTMLElement {
    ensureSandboxHelpOutsideClose();
    const wrap = document.createElement("span");
    wrap.className = "sandboxFieldHelp";
    wrap.setAttribute("aria-label", "What this does");

    const mark = document.createElement("span");
    mark.className = "sandboxFieldHelpMark";
    mark.textContent = "?";
    mark.setAttribute("aria-hidden", "true");

    const tip = document.createElement("span");
    tip.className = "sandboxFieldHelpTip";
    tip.textContent = text;
    tip.setAttribute("aria-hidden", "true");
    tip.setAttribute("role", "tooltip");

    wrap.append(mark, tip);

    const open = () => {
        closeAllSandboxFieldHelp(wrap);
        wrap.classList.add("sandboxFieldHelp--open");
        tip.setAttribute("aria-hidden", "false");
    };
    const close = () => {
        wrap.classList.remove("sandboxFieldHelp--open");
        tip.setAttribute("aria-hidden", "true");
    };

    wrap.addEventListener("pointerenter", (e) => {
        if (e.pointerType === "touch" || isCoarsePointer()) return;
        open();
    });
    wrap.addEventListener("pointerleave", (e) => {
        if (e.pointerType === "touch" || isCoarsePointer()) return;
        close();
    });
    wrap.addEventListener(
        "pointerdown",
        (e) => {
            // Touch / coarse: tap toggles. Mouse clicks do nothing special (hover handles it).
            if (e.pointerType !== "touch" && !isCoarsePointer()) return;
            e.preventDefault();
            e.stopPropagation();
            if (wrap.classList.contains("sandboxFieldHelp--open")) close();
            else open();
        },
        { passive: false },
    );

    return wrap;
}

function makeDefaultButton(
    def: SandboxFieldDef,
    cfg: SandboxConfig,
    apply: (value: unknown) => void,
): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn sandboxFieldDefaultBtn";
    btn.textContent = "Default";
    btn.title = `Restore ${def.label} to this mode's default`;
    btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = getDefaultFieldValue(cfg.baseMode, def.key);
        apply(value);
        onChange?.();
    });
    return btn;
}

function makeNumberControl(def: Extract<SandboxFieldDef, { kind: "number" }>, cfg: SandboxConfig): HTMLElement {
    const box = document.createElement("div");
    box.className = "modeBox rgbControl";
    box.dataset.fieldPath = def.key;

    const head = document.createElement("div");
    head.className = "rgbHead";
    const titleWrap = document.createElement("div");
    titleWrap.className = "sandboxFieldTitleRow";
    const h3 = document.createElement("h3");
    h3.textContent = def.label;
    titleWrap.appendChild(h3);
    if (def.hint) titleWrap.appendChild(makeFieldHelpMark(def.hint));
    const val = document.createElement("span");
    val.className = "sandboxFieldVal";
    head.append(titleWrap, val);

    const row = document.createElement("div");
    row.className = "rgbRow";
    const range = document.createElement("input");
    range.type = "range";
    range.min = String(def.min);
    range.max = String(def.max);
    range.step = String(def.step);
    range.className = "sandboxDynRange";
    const num = document.createElement("input");
    num.type = "number";
    num.min = String(def.min);
    num.max = String(def.max);
    num.step = String(def.step);
    num.className = "sandboxNumericInput sandboxDynNum";
    row.append(range, num);

    const applyValue = (raw: unknown) => {
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        const clamped = Math.max(def.min, Math.min(def.max, n));
        range.value = String(clamped);
        num.value = String(clamped);
        val.textContent = String(clamped);
    };

    box.append(head, row);
    box.appendChild(makeDefaultButton(def, cfg, applyValue));

    applyValue(getPath(cfg, def.key) ?? def.min);

    const apply = (raw: string) => {
        applyValue(raw);
        onChange?.();
    };
    range.addEventListener("input", () => apply(range.value));
    num.addEventListener("change", () => apply(num.value));
    num.addEventListener("input", () => {
        val.textContent = num.value;
    });

    return box;
}

function makeToggleControl(def: Extract<SandboxFieldDef, { kind: "toggle" }>, cfg: SandboxConfig): HTMLElement {
    const box = document.createElement("div");
    box.className = "modeBox";
    box.dataset.fieldPath = def.key;
    const titleRow = document.createElement("div");
    titleRow.className = "sandboxToggleTitleRow";
    const titleWrap = document.createElement("div");
    titleWrap.className = "sandboxFieldTitleRow";
    const h3 = document.createElement("h3");
    h3.textContent = def.label.toUpperCase();
    titleWrap.appendChild(h3);
    if (def.hint) titleWrap.appendChild(makeFieldHelpMark(def.hint));
    titleRow.appendChild(titleWrap);
    box.appendChild(titleRow);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "toggleSwitch sandboxDynToggle";
    btn.setAttribute("aria-label", def.label);
    const applyValue = (raw: unknown) => {
        syncToggleSwitch(btn, !!raw);
    };
    applyValue(getPath(cfg, def.key));
    btn.addEventListener("click", () => {
        const next = btn.getAttribute("aria-pressed") !== "true";
        syncToggleSwitch(btn, next);
        onChange?.();
    });
    box.appendChild(btn);
    box.appendChild(makeDefaultButton(def, cfg, applyValue));
    return box;
}

export function rebuildSandboxConfigForm(cfg: SandboxConfig, opts?: { onChange?: () => void }): void {
    onChange = opts?.onChange ?? onChange;
    const mount = container();
    if (!mount) return;

    const title = document.getElementById("sandboxConfigTitle");
    if (title) title.textContent = `SANDBOX — ${SANDBOX_BASE_MODE_TITLES[cfg.baseMode].toUpperCase()}`;

    closeAllSandboxFieldHelp();
    mount.replaceChildren();

    const modeSection = document.createElement("h2");
    modeSection.className = "sandboxSectionTitle";
    modeSection.textContent = "Mode parameters";
    mount.appendChild(modeSection);

    for (const def of modeFieldDefs(cfg.baseMode)) {
        mount.appendChild(def.kind === "number" ? makeNumberControl(def, cfg) : makeToggleControl(def, cfg));
    }

    const commonSection = document.createElement("h2");
    commonSection.className = "sandboxSectionTitle";
    commonSection.textContent = "Overlays & shared";
    mount.appendChild(commonSection);

    for (const def of commonFieldDefsForMode(cfg.baseMode)) {
        mount.appendChild(def.kind === "number" ? makeNumberControl(def, cfg) : makeToggleControl(def, cfg));
    }

    builtForMode = cfg.baseMode;
    syncSandboxCustomizeButtons(cfg);
}

/** Show Customize for SpeedRun / Golf base modes (SpeedRun Layer toggle removed). */
export function syncSandboxCustomizeButtons(cfg: SandboxConfig): void {
    const srBtn = document.getElementById("sandboxSpeedrunCustomizeBtn");
    if (srBtn) srBtn.classList.toggle("hidden", cfg.baseMode !== "speedrun");
    const golfBtn = document.getElementById("sandboxPenaltyCustomizeBtn");
    if (golfBtn) golfBtn.classList.toggle("hidden", cfg.baseMode !== "penalty");
}

/** @deprecated use syncSandboxCustomizeButtons */
export function syncSpeedrunCustomizeVisibility(cfg: SandboxConfig): void {
    syncSandboxCustomizeButtons(cfg);
}

/** Read current form values into a config (keeps layout blobs from base). */
export function readSandboxConfigFromForm(base: SandboxConfig): SandboxConfig {
    let cfg = normalizeSandboxConfig(base);
    const mount = container();
    if (!mount) return cfg;

    mount.querySelectorAll<HTMLElement>("[data-field-path]").forEach((box) => {
        const path = box.dataset.fieldPath;
        if (!path) return;
        const toggle = box.querySelector<HTMLElement>(".sandboxDynToggle");
        if (toggle) {
            cfg = setPath(cfg, path, toggle.getAttribute("aria-pressed") === "true");
            return;
        }
        const num = box.querySelector<HTMLInputElement>(".sandboxDynNum");
        if (num) {
            const n = Number(num.value);
            if (Number.isFinite(n)) cfg = setPath(cfg, path, n);
        }
    });

    // Preserve layout blobs (not editable via form fields)
    cfg.common.speedrunLayout = base.common.speedrunLayout;
    cfg.common.penaltyLayout = base.common.penaltyLayout;
    // Force SpeedRun play flag from base mode (no UI toggle)
    cfg.common.speedrunMode = cfg.baseMode === "speedrun";
    syncSandboxCustomizeButtons(cfg);
    return normalizeSandboxConfig(cfg);
}

export function sandboxFormBuiltFor(): string | null {
    return builtForMode;
}

/** Unused import guard helper for callers that need a fresh defaults object. */
export function freshDefaultsForMode(baseMode: SandboxConfig["baseMode"]): SandboxConfig {
    return defaultSandboxConfig(baseMode);
}
